import React, { useEffect, useState } from 'react';
import { Checkbox, FormControl, MenuItem, Paper, Select } from '@mui/material';
import { styled } from '@mui/system'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import Loader from '../../components/Loader/Loader'
import Toptag from '../../components/toptag/Toptag'
import apiFunctions from '../../api/apiFunctions'
import "./rewardConfig.css"

const NavPaper = styled(Paper)({
    padding: "5px 12px",
    borderRadius: "0",
    backgroundColor: "white",
    boxShadow: "rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px"
})

const DEFAULT_BONUS_TAG_ID = '68be9fd7d510336b5947bc08';
const DEFAULT_BONUS_EMISSION = 600000;
const DEFAULT_BONUS_MULTIPLIER = 2;

function mapTagIds(tagIds, fallback = []) {
    if (!Array.isArray(tagIds)) {
        return fallback;
    }
    return tagIds.map((t) => (typeof t === 'object' ? String(t._id) : String(t)));
}

export default function AddRewardConfig() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [err, setErr] = useState(false);
    const [allTags, setAllTags] = useState([]);

    const [obj, setObj] = useState({
        minimum_users_required: '',
        amount_per_hour: '',
        bonus_multiplier: DEFAULT_BONUS_MULTIPLIER,
        bonus_emission_threshold: DEFAULT_BONUS_EMISSION,
        bonus_tag_ids: [DEFAULT_BONUS_TAG_ID],
        emissionNone: false,
        tagsNone: false,
        min_emission_required: '',
        minEmissionNone: true,
        special_events_rewards_enabled: true,
        exclude_tag_ids: [],
    })

    const loadDefaults = async () => {
        setIsLoading(true);
        try {
            const [activeResult, tagsResult] = await Promise.all([
                apiFunctions.getActiveRewardConfig(),
                apiFunctions.getAllTag(500, '', 1),
            ]);

            if (tagsResult.data.status) {
                setAllTags(tagsResult.data.data || []);
            }

            if (activeResult.data.status && activeResult.data.data) {
                const active = activeResult.data.data;
                const tagIds = mapTagIds(active.bonus_tag_ids, [DEFAULT_BONUS_TAG_ID]);
                const excludeTagIds = mapTagIds(active.exclude_tag_ids, []);
                const hasMinEmission = active.min_emission_required != null;

                setObj({
                    minimum_users_required: active.minimum_users_required ?? '',
                    amount_per_hour: active.amount_per_hour ?? '',
                    bonus_multiplier: active.bonus_multiplier ?? DEFAULT_BONUS_MULTIPLIER,
                    bonus_emission_threshold: active.bonus_emission_threshold ?? DEFAULT_BONUS_EMISSION,
                    bonus_tag_ids: tagIds,
                    emissionNone: active.bonus_emission_threshold === null,
                    tagsNone: Array.isArray(active.bonus_tag_ids) && active.bonus_tag_ids.length === 0,
                    min_emission_required: hasMinEmission ? active.min_emission_required : '',
                    minEmissionNone: !hasMinEmission,
                    special_events_rewards_enabled: active.special_events_rewards_enabled !== false,
                    exclude_tag_ids: excludeTagIds,
                });
            }
        } catch (error) {
            toast.error("Failed to load config defaults", {
                position: toast.POSITION.TOP_RIGHT
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadDefaults();
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErr(false);

        if (!obj.minimum_users_required || obj.amount_per_hour === '') {
            setErr(true);
            return;
        }

        let configObj = {
            minimum_users_required: parseInt(obj.minimum_users_required, 10),
            amount_per_hour: parseFloat(obj.amount_per_hour),
            bonus_multiplier: parseFloat(obj.bonus_multiplier) || DEFAULT_BONUS_MULTIPLIER,
            bonus_emission_threshold: obj.emissionNone ? null : parseFloat(obj.bonus_emission_threshold),
            bonus_tag_ids: obj.tagsNone ? [] : obj.bonus_tag_ids,
            min_emission_required: obj.minEmissionNone ? null : parseFloat(obj.min_emission_required),
            special_events_rewards_enabled: !!obj.special_events_rewards_enabled,
            exclude_tag_ids: obj.exclude_tag_ids || [],
        }

        setIsLoading(true);
        try {
            const result = await apiFunctions.createRewardConfig(configObj);

            if (!result.data.status) {
                if (result.data.code === 401) {
                    localStorage.clear();
                    navigate("/login")
                    toast.info("Session Expired Please login again!!")
                }
                toast.error(result.data.message, {
                    position: toast.POSITION.TOP_RIGHT
                });
            } else {
                toast.success("Reward Config created successfully and activated!")
                setTimeout(() => {
                    navigate("/home/reward-config");
                }, 1000);
            }
        } catch (error) {
            if (error?.response?.status === 401) {
                localStorage.clear();
                navigate("/login")
                toast.info("Session Expired Please login again!!")
            }
            toast.error(error?.response?.data?.message || "Something went wrong", {
                position: toast.POSITION.TOP_RIGHT
            });
        } finally {
            setIsLoading(false);
        }
    };

    const addConfigData = (e) => {
        const { name, value } = e.target;
        setObj({
            ...obj,
            [name]: value,
        });
    }

    const handleTagChange = (e) => {
        const value = e.target.value;
        setObj({
            ...obj,
            bonus_tag_ids: typeof value === 'string' ? value.split(',') : value,
            tagsNone: false,
        });
    };

    const handleExcludeTagChange = (e) => {
        const value = e.target.value;
        setObj({
            ...obj,
            exclude_tag_ids: typeof value === 'string' ? value.split(',') : value,
        });
    };

    const toggleEmissionNone = (e) => {
        setObj({
            ...obj,
            emissionNone: e.target.checked,
        });
    };

    const toggleMinEmissionNone = (e) => {
        setObj({
            ...obj,
            minEmissionNone: e.target.checked,
        });
    };

    const toggleSpecialEventsEnabled = (e) => {
        setObj({
            ...obj,
            special_events_rewards_enabled: e.target.checked,
        });
    };

    const toggleTagsNone = (e) => {
        setObj({
            ...obj,
            tagsNone: e.target.checked,
            bonus_tag_ids: e.target.checked ? [] : (obj.bonus_tag_ids.length ? obj.bonus_tag_ids : [DEFAULT_BONUS_TAG_ID]),
        });
    };

    const getTagTitle = (tagId) => {
        const tag = allTags.find((t) => String(t._id) === String(tagId));
        return tag ? tag.title : tagId;
    };

    return (
        <>
            <div className='container'>
                {isLoading ? (
                    <Loader />
                ) : (
                    <>
                        <Toptag />
                        <ToastContainer autoClose={1000} />
                        <NavPaper>
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                <div className='popHeading'>Create New Reward Config</div>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div style={{ padding: "20px" }}>
                                    <div style={{ marginBottom: "28px" }}>
                                        <div style={{ fontSize: "15px", fontWeight: "600", marginBottom: "4px" }}>
                                            Reward Generation
                                        </div>
                                        <div style={{ fontSize: "13px", color: "#666", marginBottom: "16px" }}>
                                            Controls whether rewards are created for a slot (attendance floor and eligibility gates).
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "start", gap: "30px", alignItems: "start", flexWrap: "wrap" }}>
                                            <div style={{ flex: "1", minWidth: "300px" }}>
                                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: "10px", alignItems: "flex-start" }}>
                                                    <div className="popText">Minimum Users Required *</div>
                                                    <div style={{ width: "100%" }}>
                                                        <input
                                                            type="number"
                                                            className="popInput"
                                                            name="minimum_users_required"
                                                            id="minimum_users_required"
                                                            value={obj.minimum_users_required}
                                                            onChange={addConfigData}
                                                            placeholder="5"
                                                            min="1"
                                                            style={{ width: "100%", maxWidth: "100%" }}
                                                        />
                                                    </div>
                                                    {err && obj.minimum_users_required === '' ? <div className="errText">Minimum users required is mandatory!</div> : null}
                                                </div>
                                            </div>

                                            <div style={{ flex: "1", minWidth: "300px" }}>
                                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: "10px", alignItems: "flex-start" }}>
                                                    <div className="popText">Hourly Reward (USD) *</div>
                                                    <div style={{ width: "100%" }}>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            className="popInput"
                                                            name="amount_per_hour"
                                                            id="amount_per_hour"
                                                            value={obj.amount_per_hour}
                                                            onChange={addConfigData}
                                                            placeholder="3.00"
                                                            min="0"
                                                            style={{ width: "100%", maxWidth: "100%" }}
                                                        />
                                                    </div>
                                                    {err && obj.amount_per_hour === '' ? <div className="errText">Hourly USD rate is mandatory!</div> : null}
                                                </div>
                                            </div>

                                            <div style={{ flex: "1", minWidth: "300px" }}>
                                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: "10px", alignItems: "flex-start" }}>
                                                    <div className="popText">Min CO2 for Reward Generation</div>
                                                    <div style={{ width: "100%" }}>
                                                        <input
                                                            type="number"
                                                            className="popInput"
                                                            name="min_emission_required"
                                                            id="min_emission_required"
                                                            value={obj.min_emission_required}
                                                            onChange={addConfigData}
                                                            placeholder="0"
                                                            min="0"
                                                            disabled={obj.minEmissionNone}
                                                            style={{ width: "100%", maxWidth: "100%" }}
                                                        />
                                                    </div>
                                                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                                                        <Checkbox checked={obj.minEmissionNone} onChange={toggleMinEmissionNone} size="small" />
                                                        None (no emission floor)
                                                    </label>
                                                </div>
                                            </div>

                                            <div style={{ flex: "1", minWidth: "300px" }}>
                                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: "10px", alignItems: "flex-start" }}>
                                                    <div className="popText">Special Events</div>
                                                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                                                        <Checkbox
                                                            checked={obj.special_events_rewards_enabled}
                                                            onChange={toggleSpecialEventsEnabled}
                                                            size="small"
                                                        />
                                                        Allow rewards for Special events
                                                    </label>
                                                </div>
                                            </div>

                                            <div style={{ flex: "1", minWidth: "300px" }}>
                                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: "10px", alignItems: "flex-start" }}>
                                                    <div className="popText">Exclude Tags (no rewards)</div>
                                                    <FormControl variant="standard" sx={{ width: "100%" }}>
                                                        <Select
                                                            multiple
                                                            value={obj.exclude_tag_ids}
                                                            onChange={handleExcludeTagChange}
                                                            displayEmpty
                                                            renderValue={(selected) => {
                                                                if (!selected.length) return 'None (all tags eligible)';
                                                                return selected.map((id) => getTagTitle(id)).join(', ');
                                                            }}
                                                            sx={{ width: "100%" }}
                                                        >
                                                            {allTags.map((tag) => (
                                                                <MenuItem key={`exclude-${tag._id}`} value={String(tag._id)}>
                                                                    {tag.title}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: "1px solid #e0e0e0", paddingTop: "24px", marginBottom: "16px" }}>
                                        <div style={{ fontSize: "15px", fontWeight: "600", marginBottom: "4px" }}>
                                            Bonus Rewards
                                        </div>
                                        <div style={{ fontSize: "13px", color: "#666", marginBottom: "16px" }}>
                                            Multiplies the hourly amount when emission and/or tag bonus rules match. Does not block generation.
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "start", gap: "30px", alignItems: "start", flexWrap: "wrap" }}>
                                            <div style={{ flex: "1", minWidth: "300px" }}>
                                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: "10px", alignItems: "flex-start" }}>
                                                    <div className="popText">Bonus Multiplier *</div>
                                                    <div style={{ width: "100%" }}>
                                                        <input
                                                            type="number"
                                                            step="0.1"
                                                            className="popInput"
                                                            name="bonus_multiplier"
                                                            id="bonus_multiplier"
                                                            value={obj.bonus_multiplier}
                                                            onChange={addConfigData}
                                                            placeholder="2"
                                                            min="1"
                                                            max="10"
                                                            style={{ width: "100%", maxWidth: "100%" }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ flex: "1", minWidth: "300px" }}>
                                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: "10px", alignItems: "flex-start" }}>
                                                    <div className="popText">Minimum CO2 Emission for Bonus</div>
                                                    <div style={{ width: "100%" }}>
                                                        <input
                                                            type="number"
                                                            className="popInput"
                                                            name="bonus_emission_threshold"
                                                            id="bonus_emission_threshold"
                                                            value={obj.bonus_emission_threshold}
                                                            onChange={addConfigData}
                                                            placeholder="600000"
                                                            min="0"
                                                            disabled={obj.emissionNone}
                                                            style={{ width: "100%", maxWidth: "100%" }}
                                                        />
                                                    </div>
                                                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                                                        <Checkbox checked={obj.emissionNone} onChange={toggleEmissionNone} size="small" />
                                                        None (disable emission bonus)
                                                    </label>
                                                </div>
                                            </div>

                                            <div style={{ flex: "1", minWidth: "300px" }}>
                                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: "10px", alignItems: "flex-start" }}>
                                                    <div className="popText">Bonus Tags</div>
                                                    <FormControl variant="standard" sx={{ width: "100%" }}>
                                                        <Select
                                                            multiple
                                                            value={obj.tagsNone ? [] : obj.bonus_tag_ids}
                                                            onChange={handleTagChange}
                                                            disabled={obj.tagsNone}
                                                            displayEmpty
                                                            renderValue={(selected) => {
                                                                if (!selected.length) return 'Select tags';
                                                                return selected.map((id) => getTagTitle(id)).join(', ');
                                                            }}
                                                            sx={{ width: "100%" }}
                                                        >
                                                            {allTags.map((tag) => (
                                                                <MenuItem key={tag._id} value={String(tag._id)}>
                                                                    {tag.title}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                                                        <Checkbox checked={obj.tagsNone} onChange={toggleTagsNone} size="small" />
                                                        None (disable tag bonus)
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* <div className="popText" style={{ fontSize: "13px", color: "#666" }}>
                                        NOTE: Rewards are calculated and stored in USD. Token amounts are computed only at payout time based on the user wallet network and token. Minimum users required is the attendance gate from this config (not event required_people).
                                    </div> */}
                                </div>

                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "15px", marginBottom: "15px", gap: "15px" }}>
                                    <button type="button" className='popBtn' style={{ backgroundColor: "#6c757d", color: "white", marginRight: "10px" }} onClick={() => navigate("/home/reward-config")}>
                                        CANCEL
                                    </button>
                                    <button type="submit" className='popBtn'>
                                        CREATE
                                    </button>
                                </div>
                            </form>
                        </NavPaper>
                    </>
                )}
            </div>
        </>
    )
}
